"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.rankingEvalContract = exports.sourcesUnreachableDataSchema = exports.evalSourceInputSchema = exports.evalDebugInfoSchema = exports.rankerDetailSchema = exports.rankerSummarySchema = exports.evalSpaceSchema = exports.evalCommentRatingSchema = exports.evalComparisonSchema = exports.evalSourceSchema = exports.evalCustomRankerSchema = exports.evalSpaceSetSchema = exports.externalEvalSpaceSchema = void 0;
exports.errorCommentNotFound = errorCommentNotFound;
exports.errorInvalidConversationKey = errorInvalidConversationKey;
exports.errorUnknownSource = errorUnknownSource;
exports.errorSourcesUnreachable = errorSourcesUnreachable;
exports.errorNotScored = errorNotScored;
/**
 * Ranking-eval: internal, admin-only tooling for evaluating comment-ranking
 * algorithms — choosing a conversation set, comparing rankers side by side,
 * scoring individual comments, and managing custom ranker configs and the
 * PSI sites conversations are fetched from.
 *
 * Silo-scoped procedures operate on the module's shared maps (fetched
 * conversations, the selected set, custom rankers, sources). Instance-scoped
 * procedures carry `{instanceKey}` — the conversation key — and cover the
 * per-conversation work: importing a conversation's comments, submitting
 * side-by-side comparisons, and rating comments.
 *
 * The wire shape is deliberately pragmatic (this is internal tooling, not a
 * partner surface). Scoring/ranking runs through the same engine
 * (`server/modules/ranking.ts`) `spaces.rankingDebug` and `comments.list` use
 * for real spaces — this resource is a separate one because it acts on an
 * imported eval conversation, not a real space, even though the backend work
 * is shared.
 */
const client_1 = require("@orpc/client");
const z = __importStar(require("zod"));
const roles_capabilities_1 = require("../../roles-capabilities");
const api_schemas_1 = require("../api-schemas");
const base_1 = require("../base");
const multi_language_1 = require("../multi-language");
const schemas_1 = require("../schemas");
const comments_1 = require("./comments");
// ============================================================================
// Resource identity
// ============================================================================
/** `siloKey` + `instanceKey` (the conversation key) — a single conversation. */
const conversationParams = {
    ...api_schemas_1.siloParams,
    instanceKey: api_schemas_1.shortString,
};
const conversationReadParams = { ...conversationParams, locale: api_schemas_1.readLocaleParam };
// ============================================================================
// Wire schemas
// ============================================================================
exports.externalEvalSpaceSchema = z.object({
    key: z.string(),
    url: z.string(),
    siloKey: z.string(),
    sourceKey: z.string(),
    fetched: schemas_1.isoDateTime,
    spaceKey: z.string(),
    name: multi_language_1.multiLanguageStringSchema,
    createdAt: schemas_1.isoDateTime,
    commentCount: z.number(),
});
exports.evalSpaceSetSchema = z.object({
    conversationKeys: z.array(z.string()),
    conversationNames: z.record(z.string(), z.string()),
});
exports.evalCustomRankerSchema = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    firstPassPrompt: z.string(),
    secondPassPrompt: z.string(),
    topN: z.number(),
    model: z.string(),
    reasoningEffort: z.string(),
    enabled: z.boolean(),
});
exports.evalSourceSchema = z.object({
    key: z.string(),
    label: z.string(),
    baseUrl: z.string(),
    remoteSiloKey: z.string(),
});
exports.evalComparisonSchema = z.object({
    key: z.string(),
    from: z.string(),
    fromName: z.string(),
    conversationKey: z.string(),
    rankerA: z.string(),
    rankerB: z.string(),
    winner: z.enum(['a_better', 'tie', 'b_better']),
    timestamp: schemas_1.isoDateTime,
});
exports.evalCommentRatingSchema = z.object({
    key: z.string(),
    from: z.string(),
    fromName: z.string(),
    conversationKey: z.string(),
    commentKey: z.string(),
    rating: z.number(),
    reason: z.string().optional(),
    timestamp: schemas_1.isoDateTime,
});
exports.evalSpaceSchema = z.object({
    name: z.string().nullable(),
    importedAt: schemas_1.isoDateTime.nullable(),
});
exports.rankerSummarySchema = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
});
exports.rankerDetailSchema = z.object({
    name: z.string(),
    description: z.string(),
    firstPassPrompt: z.string().optional(),
    secondPassPrompt: z.string().optional(),
});
// Same shape as `spaces.rankingDebug.getDebugInfo`'s output — both read the
// identical first-pass-scores/second-pass-reasoning module data, just for a
// conversation instead of a space.
exports.evalDebugInfoSchema = z.object({
    scores: z.record(z.string(), z.object({ score: z.number(), reasoning: z.string().optional() })),
    secondPassReasoning: z.string().nullable(),
});
// Custom ranker as sent by the client on save: `key` is absent when creating a
// new ranker (the server mints a UUID). Prompts can legitimately be long, so
// they use `mediumText` (and `firstPassPrompt` must be non-empty).
const evalCustomRankerInputSchema = z.object({
    key: api_schemas_1.shortString.optional(),
    name: api_schemas_1.shortString,
    description: api_schemas_1.mediumText,
    firstPassPrompt: api_schemas_1.mediumText.min(1),
    secondPassPrompt: api_schemas_1.mediumText,
    topN: z.number().int().min(0).max(1000),
    model: api_schemas_1.shortString,
    reasoningEffort: api_schemas_1.shortString,
    enabled: z.boolean(),
});
const SLUG_PATTERN = /^[a-z0-9-]+$/;
/**
 * Whether `value` is just an origin: no path, query, fragment, or credentials,
 * and not a `.localhost` name, which `z.regexes.domain` lets through but
 * resolves to this machine.
 */
function isBareOrigin(value) {
    if (!URL.canParse(value))
        return false;
    const url = new URL(value);
    return url.pathname === '/' && !url.search && !url.hash && !url.username && !url.password && !url.hostname.endsWith('.localhost');
}
/**
 * A conversation source as an admin enters it.
 *
 * - Slugs can't contain `:`, so a key can't break the `::`-delimited
 *   conversation key it is embedded in.
 * - The server sends requests to `baseUrl`, so it must be https on a named
 *   host. `z.regexes.domain` refuses localhost and IP literals, including
 *   numeric forms like `2130706433` that the URL parser normalizes to IPv4.
 */
exports.evalSourceInputSchema = z.object({
    key: z.string().min(1, 'Required').max(32).regex(SLUG_PATTERN, { message: 'Use only lowercase letters, digits, and hyphens' }),
    label: z.string().min(1, 'Required').max(api_schemas_1.MAX_SHORT_STRING_LENGTH),
    baseUrl: z
        .url({
        protocol: /^https$/,
        hostname: z.regexes.domain,
        error: 'Must be an https URL on a host name, not localhost or an IP address',
    })
        .max(api_schemas_1.MAX_URL_LENGTH)
        .refine(isBareOrigin, { message: 'Must be just the origin, with no path, query, or credentials' }),
    remoteSiloKey: z
        .string()
        .min(1, 'Required')
        .max(64)
        .regex(SLUG_PATTERN, { message: 'Use only lowercase letters, digits, and hyphens' }),
});
// ============================================================================
// Typed errors
// ============================================================================
/** Thrown when a `submitCommentRating` commentKey isn't in the instance. */
function errorCommentNotFound(commentKey) {
    return new client_1.ORPCError('COMMENT_NOT_FOUND', {
        status: 404,
        data: { commentKey },
        message: 'Comment not found: ' + commentKey,
    });
}
/** Thrown when an `importConversation` instanceKey isn't a 4-part key. */
function errorInvalidConversationKey(instanceKey) {
    return new client_1.ORPCError('INVALID_CONVERSATION_KEY', {
        status: 400,
        data: { instanceKey },
        message: 'Invalid conversation key format: ' + instanceKey,
    });
}
/** Thrown when an `importConversation` key names an unknown source site. */
function errorUnknownSource(sourceKey) {
    return new client_1.ORPCError('UNKNOWN_SOURCE', {
        status: 400,
        data: { sourceKey },
        message: 'Unknown source site: ' + sourceKey,
    });
}
exports.sourcesUnreachableDataSchema = z.object({
    sources: z.array(z.object({ sourceKey: z.string(), baseUrl: z.string() })),
});
/**
 * Thrown by `refreshConversations` when none of the silo's sources could be
 * reached.
 */
function errorSourcesUnreachable(sources) {
    return new client_1.ORPCError('SOURCES_UNREACHABLE', {
        status: 502,
        data: { sources },
        message: 'Could not reach any source: ' + sources.map((source) => `${source.sourceKey} (${source.baseUrl})`).join(', '),
    });
}
/**
 * Thrown by `listConversationComments` when some top-level comments haven't
 * been scored yet for the requested ranker.
 */
function errorNotScored(rankerKey, unscored, total) {
    return new client_1.ORPCError('NOT_SCORED', {
        status: 409,
        data: { rankerKey, unscored, total },
        message: `${unscored} of ${total} comments not yet scored for ranker: ${rankerKey}`,
    });
}
// ============================================================================
// Silo-scoped procedures
// ============================================================================
const listConversations = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/conversations',
    tags: ['Ranking eval'],
    summary: 'List fetched conversations',
    description: 'List the conversations fetched from other PSI sites, each with its comment count and when it was fetched. These are ' +
        'candidates, not imported conversations: `importConversation` copies one in before it can be ranked or rated. Served from ' +
        'stored data, so it reflects whatever the last refresh found rather than the sites as they are right now.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloReadParams }))
    .output(z.array(exports.externalEvalSpaceSchema));
const refreshConversations = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/ranking-eval/conversations/refresh',
    tags: ['Ranking eval'],
    summary: 'Re-fetch conversation lists from every source',
    description: 'Re-fetch the conversation list from every site `listSources` returns and return the combined result, which also becomes ' +
        'what `listConversations` serves. A site that cannot be reached is logged and left out, so a shorter list than usual means ' +
        'a source failed rather than that conversations went away. When every site fails the call is refused with ' +
        'SOURCES_UNREACHABLE, listing each source and its base URL, and the stored list is left as it was. With no sources at ' +
        'all it returns an empty list.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloParams }))
    .output(z.array(exports.externalEvalSpaceSchema))
    .errors({
    SOURCES_UNREACHABLE: { status: 502, data: exports.sourcesUnreachableDataSchema },
});
const getSelectedSet = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/selected-set',
    tags: ['Ranking eval'],
    summary: 'Get the selected conversation set',
    description: 'Get the conversation set the dashboard is working through, with a display name per key, or null before anyone has saved ' +
        'one. The set is shared silo-wide rather than held per admin, so everyone with access to this tooling sees the same ' +
        'selection.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloReadParams }))
    .output(exports.evalSpaceSetSchema.nullable());
const updateSelectedSet = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/ranking-eval/selected-set',
    tags: ['Ranking eval'],
    summary: 'Replace the selected conversation set',
    description: 'Replace the conversation set the dashboard works through, and return what was stored. Send the whole set, keys and their ' +
        'display names together, since nothing is merged into what is already saved. The set is shared silo-wide, so this ' +
        "overwrites everyone's selection, not just your own.",
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({
    ...api_schemas_1.siloParams,
    conversationKeys: z.array(api_schemas_1.shortString).max(api_schemas_1.MAX_ARRAY_LENGTH),
    conversationNames: (0, api_schemas_1.boundedRecord)(z.string().max(api_schemas_1.MAX_SHORT_STRING_LENGTH)),
}))
    .output(exports.evalSpaceSetSchema);
const listCustomRankers = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/custom-rankers',
    tags: ['Ranking eval'],
    summary: 'List custom rankers',
    description: 'List the rankers written in this silo through the dashboard, each with its two prompts, model, reasoning effort, and ' +
        'whether it is enabled. These are separate from the built-in rankers that ship with the server, which ' +
        '`getAvailableRankers` returns.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloReadParams }))
    .output(z.array(exports.evalCustomRankerSchema));
const saveCustomRanker = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/ranking-eval/custom-rankers',
    tags: ['Ranking eval'],
    summary: 'Create or update a custom ranker',
    description: 'Write a ranker of your own and return it as stored. Omit `key` to create one and the server mints it; send a key to ' +
        'replace that ranker wholesale, since nothing is merged. `firstPassPrompt` scores each comment on its own and must not be ' +
        'empty; `secondPassPrompt` re-ranks the best `topN` of them together. Editing a prompt does not re-score comments that ' +
        'already carry a score for this ranker, and re-running the first pass will not either, since it only scores comments with ' +
        'no score yet. Save the edit under a new ranker to score a conversation from scratch.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloParams, ranker: evalCustomRankerInputSchema }))
    .output(exports.evalCustomRankerSchema);
const deleteCustomRanker = base_1.baseProcedure
    .route({
    method: 'DELETE',
    path: '/silo/{siloKey}/ranking-eval/custom-rankers/{rankerKey}',
    tags: ['Ranking eval'],
    summary: 'Delete a custom ranker',
    description: 'Delete a ranker written through the dashboard. `deleted` is always true, including for a key that was never there, so ' +
        'there is nothing to branch on. The scores, comparisons, and ratings recorded against that ranker live outside it and are ' +
        'left in place, so deleting a ranker does not erase the evidence gathered with it.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloParams, rankerKey: api_schemas_1.shortString }))
    .output(z.object({ deleted: z.boolean() }));
const listSources = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/sources',
    tags: ['Ranking eval'],
    summary: 'List conversation sources',
    description: 'List the PSI sites this silo fetches conversations from, oldest first. `refreshConversations` reads from every one of ' +
        'them, and each conversation it returns names its source in `sourceKey`.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloReadParams }))
    .output(z.array(exports.evalSourceSchema));
const saveSource = base_1.baseProcedure
    .route({
    method: 'PUT',
    path: '/silo/{siloKey}/ranking-eval/sources/{sourceKey}',
    tags: ['Ranking eval'],
    summary: 'Create or update a conversation source',
    description: 'Add a site to fetch conversations from, or replace the label, base URL, and remote silo of an existing one, and return ' +
        'the source as stored. `sourceKey` becomes part of every conversation key fetched from the site, so it cannot be renamed: ' +
        'delete the source and add it again under a new key. `baseUrl` must be an `https` origin with no path, on a host name ' +
        'rather than `localhost` or an IP address, since the server sends requests to it; the API is reached at `baseUrl` plus ' +
        '`/api`. `remoteSiloKey` is the silo to read on that site.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({
    ...api_schemas_1.siloParams,
    sourceKey: exports.evalSourceInputSchema.shape.key,
    label: exports.evalSourceInputSchema.shape.label,
    baseUrl: exports.evalSourceInputSchema.shape.baseUrl,
    remoteSiloKey: exports.evalSourceInputSchema.shape.remoteSiloKey,
}))
    .output(exports.evalSourceSchema);
const deleteSource = base_1.baseProcedure
    .route({
    method: 'DELETE',
    path: '/silo/{siloKey}/ranking-eval/sources/{sourceKey}',
    tags: ['Ranking eval'],
    summary: 'Delete a conversation source',
    description: 'Stop fetching conversations from a site. `deleted` is always true, including for a key that was never there. ' +
        'Conversations already imported from the site stay readable, and its conversations drop out of `listConversations` at ' +
        'the next refresh.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloParams, sourceKey: api_schemas_1.shortString }))
    .output(z.object({ deleted: z.boolean() }));
const getAvailableRankers = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/rankers',
    tags: ['Ranking eval'],
    summary: 'List built-in rankers',
    description: 'List the rankers that ship with the server, each with a name and a description of what it rewards. These are configured ' +
        'in the repository rather than through the dashboard, so they cannot be edited here; `getRankerDetails` returns one with ' +
        'the prompts it runs. Not every ranker uses an LLM: the default orders by reactions and recency alone.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloReadParams }))
    .output(z.array(exports.rankerSummarySchema));
const getRankerDetails = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/rankers/{rankerKey}',
    tags: ['Ranking eval'],
    summary: 'Get a built-in ranker with its prompts',
    description: 'Get one built-in ranker with the prompts it runs: the first pass that scores each comment on its own, and the second pass ' +
        'that re-ranks the strongest ones together. Both prompts are absent for a ranker that uses no LLM. An unknown key is ' +
        'refused with NOT_FOUND.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...api_schemas_1.siloReadParams, rankerKey: api_schemas_1.shortString }))
    .output(exports.rankerDetailSchema);
// ============================================================================
// Instance-scoped procedures (instanceKey = conversation key)
// ============================================================================
const importConversation = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/import',
    tags: ['Ranking eval'],
    summary: "Import a conversation's comments and authors",
    description: 'Import a fetched conversation so its comments can be ranked and rated here, and return how many comments and authors were ' +
        'written. The copy mirrors what a reader would see: deleted comments, comments in moderation, and empty ones such as a ' +
        'slider vote with no text are all left out. Importing a conversation that is already here does nothing and returns the ' +
        'counts it already has, unless you set `force`, which wipes the local copy and imports again. Scores, ratings, and ' +
        'comparisons are stored outside the copy, so they survive a forced re-import. A key that is not four parts is refused ' +
        'with INVALID_CONVERSATION_KEY, and one naming a site this silo does not know with UNKNOWN_SOURCE.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...conversationParams, force: z.boolean().optional() }))
    .output(z.object({ commentCount: z.number(), personaCount: z.number() }))
    .errors({
    INVALID_CONVERSATION_KEY: { status: 400, data: z.object({ instanceKey: z.string() }) },
    UNKNOWN_SOURCE: { status: 400, data: z.object({ sourceKey: z.string() }) },
});
const getConversation = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/conversation',
    tags: ['Ranking eval'],
    summary: "Get a conversation's name and import status",
    description: "Get the imported conversation's stored name and when it was imported. `name` is null until an import has run, and a " +
        'client falls back to the conversation key; `importedAt` is null until the first import, which is how you tell an ' +
        'un-imported conversation from an empty one.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...conversationReadParams }))
    .output(exports.evalSpaceSchema);
const runFirstPass = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/first-pass',
    tags: ['Ranking eval'],
    summary: 'Score a batch of comments for a ranker',
    description: "Run the first pass, scoring the conversation's comments one at a time against a ranker's criteria. Each call scores as " +
        'many as fit in one request and reports how many are left, so keep calling while `remaining` is above zero, then run the ' +
        'second pass. Only comments with no score yet are scored, so a repeat call is cheap and a scored comment is never ' +
        'rescored. A ranker with no LLM pass reports complete having done nothing.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...conversationParams, rankerKey: api_schemas_1.shortString }))
    .output(z.object({
    status: z
        .enum(['in_progress', 'already_in_progress', 'scoring_complete'])
        .describe('`in_progress` — this call scored a batch and more remain; call again. `already_in_progress` — another call for this ranker is still running; wait and retry. `scoring_complete` — every comment is scored.'),
    scored: z.number().describe('Comments scored by this call (bounded per call to avoid a request timeout).'),
    total: z.number().describe('Total comments in the conversation.'),
    remaining: z.number().describe('Comments still unscored after this call. Keep calling while this is greater than 0.'),
}));
const runSecondPass = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/second-pass',
    tags: ['Ranking eval'],
    summary: 'Re-rank the top-scored comments for a ranker',
    description: 'Run the second pass, sending the best `topN` comments to a stronger model together so it can weigh them against each ' +
        'other rather than in isolation. This is what turns individual scores into an ordering. Run it once the first pass reports ' +
        'that scoring is complete. `cached` is true when the answer was already worked out and nothing was re-run.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...conversationParams, rankerKey: api_schemas_1.shortString }))
    .output(z.object({ status: z.literal('complete'), cached: z.boolean().optional() }));
const getDebugInfo = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/debug-info',
    tags: ['Ranking eval'],
    summary: "Get a ranker's scores and reasoning",
    description: 'Get what a ranker made of this conversation: the score and reasoning it gave each comment in the first pass, plus the ' +
        'reasoning behind the second pass over the top set. This is how you tell a ranker that ordered badly from one that ' +
        'explained itself badly. The same data `spaces.rankingDebug` returns for a real space.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...conversationReadParams, rankerKey: api_schemas_1.shortString }))
    .output(exports.evalDebugInfoSchema);
const listComparisons = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/comparisons',
    tags: ['Ranking eval'],
    summary: 'List comparison votes for a conversation',
    description: 'List the side-by-side votes recorded on this conversation, each naming the two rankers compared, which one won, and who ' +
        'voted. Votes are visible to everyone with access to this tooling, not only to the person who cast them, since the point ' +
        'is to see whether reviewers agree.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...conversationReadParams }))
    .output(z.array(exports.evalComparisonSchema));
const submitComparison = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/comparisons',
    tags: ['Ranking eval'],
    summary: 'Submit a side-by-side comparison vote',
    description: 'Record which of two rankers ordered this conversation better, or that they tied, and return the key of the vote. One vote ' +
        'is kept per person per pair of rankers, so voting again replaces your earlier one rather than adding to it. The pair is ' +
        'ordered: a vote on A against B is stored separately from one on B against A, so keep the sides consistent if you want ' +
        'votes to accumulate on the same matchup.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({
    ...conversationParams,
    rankerA: api_schemas_1.shortString,
    rankerB: api_schemas_1.shortString,
    winner: z.enum(['a_better', 'tie', 'b_better']),
}))
    .output(z.object({ key: z.string() }));
const getCommentRatings = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/comment-ratings',
    tags: ['Ranking eval'],
    summary: 'List comment ratings for a conversation',
    description: 'List the human ratings recorded on this conversation, each with its score out of five, the reason if one was given, and ' +
        'who rated it. These are the baseline a ranker is judged against. Like comparisons, they are visible to everyone with ' +
        'access to this tooling.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...conversationReadParams }))
    .output(z.array(exports.evalCommentRatingSchema));
const listConversationComments = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/comments',
    tags: ['Ranking eval'],
    summary: "Rank an imported conversation's comments",
    description: 'Rank the imported comments by `sort` and return them as a tree, through the same engine that orders comments in a real ' +
        'space, so what you review here is what a reader would get. For an LLM or custom ranker every top-level comment has to be ' +
        'scored first: until then the call is refused with NOT_SCORED, which carries how many are missing so a client can show ' +
        'progress and run the first pass.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({ ...conversationReadParams, sort: api_schemas_1.shortString }))
    .output(z.object({ items: z.array(comments_1.commentTreeNodeSchema), nextCursor: z.string().nullable() }))
    .errors({
    NOT_SCORED: { status: 409, data: z.object({ rankerKey: z.string(), unscored: z.number(), total: z.number() }) },
});
const submitCommentRating = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/ranking-eval/{instanceKey}/comment-ratings',
    tags: ['Ranking eval'],
    summary: 'Submit a 1-5 rating for a comment',
    description: 'Rate one comment from 1 to 5, with an optional reason, building the human baseline a ranker can be measured against. ' +
        'Returns the key of the rating. One rating is kept per person per comment, so rating again replaces your earlier one. A ' +
        'comment key that is not in this conversation is refused with COMMENT_NOT_FOUND.',
})
    .meta({ access: 'admin', capability: roles_capabilities_1.RANKING_EVAL_CAPABILITY })
    .input(z.object({
    ...conversationParams,
    commentKey: api_schemas_1.shortString,
    rating: z.number().int().min(1).max(5),
    reason: api_schemas_1.mediumText.optional(),
}))
    .output(z.object({ key: z.string() }))
    .errors({
    COMMENT_NOT_FOUND: { status: 404, data: z.object({ commentKey: z.string() }) },
});
exports.rankingEvalContract = {
    listConversations,
    refreshConversations,
    getSelectedSet,
    updateSelectedSet,
    listCustomRankers,
    saveCustomRanker,
    deleteCustomRanker,
    listSources,
    saveSource,
    deleteSource,
    getAvailableRankers,
    getRankerDetails,
    importConversation,
    getConversation,
    listComparisons,
    submitComparison,
    getCommentRatings,
    submitCommentRating,
    listConversationComments,
    runFirstPass,
    runSecondPass,
    getDebugInfo,
};
//# sourceMappingURL=ranking-eval.js.map